/** Inline plan cheat sheet preview for the agent review Artefacts step. */

import { useEffect, useState } from "react";

import * as AgenticV2API from "@/services/api/agentic-v2";
import { cheatSheetInlinePreviewUrl } from "./cheatSheetPreviewUrl";
import type { ArtefactReviewItem } from "./types";

interface CheatSheetArtefactPreviewProps {
  artefact: ArtefactReviewItem;
  className?: string;
}

export function CheatSheetArtefactPreview({ artefact, className }: CheatSheetArtefactPreviewProps) {
  const [inlineUrl, setInlineUrl] = useState<string | null>(() =>
    cheatSheetInlinePreviewUrl(artefact),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const immediate = cheatSheetInlinePreviewUrl(artefact);
    if (immediate) {
      setInlineUrl(immediate);
      setError(null);
      return;
    }

    setInlineUrl(null);
    setError(null);
    void AgenticV2API.getArtifact(artefact.artifact_id)
      .then((loaded) => {
        if (cancelled) return;
        const url = cheatSheetInlinePreviewUrl(artefact, loaded);
        if (!url) {
          setError("Preview unavailable — this cheat sheet has no local report config.");
          return;
        }
        setInlineUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load cheat sheet preview.");
      });

    return () => {
      cancelled = true;
    };
  }, [artefact]);

  if (error) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        {error}
      </div>
    );
  }

  if (!inlineUrl) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        Loading preview…
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white text-slate-900 ring-1 ring-white/10 ${className ?? ""}`}
    >
      <iframe
        title={`Cheat sheet preview — ${artefact.title}`}
        src={inlineUrl}
        className="min-h-0 flex-1 w-full border-0 bg-white"
      />
    </div>
  );
}
